import { LegalPage, type LegalSection } from "./LegalPage";

/**
 * Terms of Use — editorial boilerplate for the public-facing website.
 *
 * Distinct from the Engagement Letter / Brokerage Agreement clients
 * sign when they instruct us. Those are separate, transaction-level
 * contracts; this page covers acceptable use of hivehomes.ae and the
 * informational content it presents.
 *
 * The copy aligns with:
 *   - Dubai Law No. 26 of 2007 regulating real estate brokers in Dubai
 *   - Federal Decree-Law No. 34 of 2021 on Combating Rumours and
 *     Cybercrimes
 *   - Standard UAE e-commerce / electronic-transactions practice
 */

const SECTIONS: LegalSection[] = [
  {
    id: "acceptance",
    heading: "Acceptance of These Terms",
    body: (
      <>
        <p>
          These Terms of Use govern your use of hivehomes.ae and any
          related services, content, and communications provided by Hive
          Homes Real Estate Brokerage L.L.C. ("Hive Homes", "we" or "our").
        </p>
        <p>
          By accessing this website, requesting information, sending an
          enquiry, or otherwise engaging with our content, you confirm that
          you have read, understood, and agreed to these Terms. If you do
          not agree, please do not use this website.
        </p>
      </>
    ),
  },
  {
    id: "our-licence",
    heading: "About Hive Homes",
    body: (
      <p>
        Hive Homes is a real estate brokerage licensed by the Dubai Land
        Department and registered with the Real Estate Regulatory Agency
        ("RERA"). Our broker registration details and licence number are
        published in the footer of every page on this website. Our office
        in Dubai is staffed during the published business hours; outside
        those hours we respond by email or WhatsApp as soon as possible.
      </p>
    ),
  },
  {
    id: "use-of-content",
    heading: "Use of the Website and Content",
    body: (
      <>
        <p>You may use this website for the following purposes:</p>
        <ul>
          <li>
            Browsing the property listings and market intelligence we have
            published for the general public.
          </li>
          <li>
            Sending us an enquiry, requesting a viewing, or asking for a
            valuation.
          </li>
          <li>
            Saving properties to your shortlist or compare tray for your
            personal reference.
          </li>
          <li>
            Sharing individual pages with friends, family, or
            professional advisors where this is reasonable and
            non-commercial.
          </li>
        </ul>
        <p>You agree not to:</p>
        <ul>
          <li>
            Scrape, harvest, or systematically extract property data,
            photography, or contact information from this website by
            automated or manual means without our prior written consent.
          </li>
          <li>
            Republish our property descriptions, photographs, market
            reports, or brand assets on another website, portal, social
            channel, or marketing material without permission.
          </li>
          <li>
            Use the website in a way that could damage, disable, overburden,
            or impair it, or interfere with another user's access.
          </li>
          <li>
            Submit enquiries you do not intend to follow through on, or
            impersonate another person when contacting our team.
          </li>
          <li>
            Upload or transmit any content that is unlawful, threatening,
            defamatory, obscene, or otherwise prohibited under UAE law,
            including Federal Decree-Law No. 34 of 2021 on Combating
            Rumours and Cybercrimes.
          </li>
        </ul>
      </>
    ),
  },
  {
    id: "listings-accuracy",
    heading: "Accuracy of Listings and Market Information",
    body: (
      <>
        <p>
          We work hard to keep our property listings current. Listing
          details — including price, availability, configuration, area,
          and photography — are drawn from developer sources, listing
          owners, and our own viewings, and are typically refreshed within
          twenty-four hours of any change we become aware of.
        </p>
        <p>
          That said, real estate is a fast-moving market. Properties may
          go under offer, prices may move, and details published here are
          for general guidance only. Nothing on this website constitutes
          an offer to sell or lease, a contract, or a financial or legal
          recommendation. Before you commit to any property we will share
          the latest verified information directly with you and your
          advisors.
        </p>
        <p>
          Market reports and insights articles reflect our view at the
          time of publication. Past performance, indicative yields, and
          forecasts are not promises of future performance.
        </p>
      </>
    ),
  },
  {
    id: "third-party-links",
    heading: "Third-Party Links and Services",
    body: (
      <p>
        Where this website links to developer microsites, news articles,
        regulatory pages, Google Maps, WhatsApp, or other third-party
        services, those destinations are not under our control. We have
        no responsibility for their content, accuracy, or availability,
        and your use of them is governed by their own terms.
      </p>
    ),
  },
  {
    id: "intellectual-property",
    heading: "Intellectual Property",
    body: (
      <p>
        The Hive Homes name, logo, brand marks, website design, original
        photography, written content, and the underlying source code are
        owned by Hive Homes or licensed to us. They are protected by UAE
        copyright, trademark, and database-right laws. Nothing on this
        website transfers any of those rights to you. Listing photography
        owned by developers or third parties is published with permission
        and remains the property of the relevant owner.
      </p>
    ),
  },
  {
    id: "your-content",
    heading: "Information You Submit",
    body: (
      <p>
        When you send us an enquiry, submit a valuation request, or list
        a property with us, you confirm that the information you provide
        is accurate, complete, and that you have the right to share it.
        How we handle that information is set out in our Privacy Policy.
      </p>
    ),
  },
  {
    id: "liability",
    heading: "Disclaimer and Limitation of Liability",
    body: (
      <>
        <p>
          To the fullest extent permitted by UAE law, the website and its
          content are provided on an "as is" basis. Hive Homes makes no
          warranty, express or implied, that the website will be
          uninterrupted, error-free, or free of viruses or other harmful
          components, or that any specific result will be obtained from
          using it.
        </p>
        <p>
          Hive Homes will not be liable for any indirect, incidental,
          consequential, or special losses arising from your use of, or
          inability to use, this website, including any reliance on
          listing or market information published here. Nothing in these
          Terms limits any liability that cannot be lawfully limited under
          UAE law, including in cases of fraud or wilful misconduct.
        </p>
        <p>
          Where we represent you in a transaction, our duties to you are
          governed by the signed engagement letter or brokerage agreement
          between us, the RERA Code of Ethics, and applicable UAE law —
          not by this website.
        </p>
      </>
    ),
  },
  {
    id: "governing-law",
    heading: "Governing Law and Jurisdiction",
    body: (
      <p>
        These Terms, and any dispute or claim arising out of or in
        connection with them, are governed by the laws of the Emirate of
        Dubai and the applicable federal laws of the United Arab Emirates.
        Any dispute will be subject to the exclusive jurisdiction of the
        courts of Dubai.
      </p>
    ),
  },
  {
    id: "updates",
    heading: "Changes to These Terms",
    body: (
      <p>
        We may update these Terms from time to time to reflect changes in
        our service or in applicable law. The current version is the one
        published on this page; the "last updated" date shows when it
        last changed. By continuing to use the website after a change,
        you accept the revised Terms.
      </p>
    ),
  },
];

export function TermsPage() {
  return (
    <LegalPage
      eyebrow="Terms of Use"
      title="A Considered Use of the Website."
      intro="These terms set out how Hive Homes' website may be used, how we handle the information on it, and the basis on which we publish market and property information. They sit alongside — not in place of — any engagement letter you sign when we represent you."
      lastUpdated="May 2026"
      sections={SECTIONS}
      seoTitle="Terms of Use | Hive Homes Dubai"
      seoDescription="The terms under which you may use hivehomes.ae, including how we publish property and market information, your responsibilities, and our liability."
    />
  );
}
